import React, { useState, useEffect } from 'react';
import EventCard from './EventCard';
import BusinessCard from './BusinessCard';

/**
 * Search Component
 * Real-time search across events and businesses
 * Filter by type (all/events/businesses)
 */
export default function Search({ events, businesses, resources = [], locations, analytics, initialQuery = '' }) {
  const [query, setQuery] = useState(initialQuery);
  const [activeFilter, setActiveFilter] = useState('all');
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    let searchResults = [];

    // Search events
    if (activeFilter === 'all' || activeFilter === 'events') {
      const eventResults = events
        .filter(event => {
          const tags = event.tags || [];
          const tagsText = tags.join(' ');
          const searchText = `${event.title} ${event.description || ''} ${tagsText}`.toLowerCase();
          return searchText.includes(lowerQuery);
        })
        .map(event => ({ type: 'event', data: event }));
      searchResults = searchResults.concat(eventResults);
    }

    // Search businesses
    if (activeFilter === 'all' || activeFilter === 'businesses') {
      const businessResults = businesses
        .filter(business => {
          const searchText = `${business.title} ${business.description || ''} ${business.category || ''}`.toLowerCase();
          return searchText.includes(lowerQuery);
        })
        .map(business => ({ type: 'business', data: business }));
      searchResults = searchResults.concat(businessResults);
    }

    // Search resources
    if (activeFilter === 'all' || activeFilter === 'resources') {
      const resourceResults = resources
        .filter(resource => {
          const searchText = `${resource.title} ${resource.description || ''}`.toLowerCase();
          return searchText.includes(lowerQuery);
        })
        .map(resource => {
          const host = resource.business_id ? businesses.find(b => b.id === resource.business_id) : null;
          return {
            type: 'business',
            data: {
              ...resource,
              isResource: true,
              type: 'service',
              locationId: resource.location_id,
              hostTitle: host ? host.title : null
            }
          };
        });
      searchResults = searchResults.concat(resourceResults);
    }

    setResults(searchResults);

    // Track search query (debounced)
    const timeoutId = setTimeout(() => {
      if (analytics && query.length >= 3) {
        analytics.trackInteraction('search', { query, filter: activeFilter, resultCount: searchResults.length });
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [query, activeFilter, events, businesses, resources, analytics]);

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    if (analytics) {
      analytics.trackInteraction('search_filter', { filter: filterId });
    }
  };

  const filters = [
    { id: 'all', label: 'All Results' },
    { id: 'events', label: 'Events Only' },
    { id: 'businesses', label: 'Businesses Only' },
    { id: 'resources', label: 'Resources Only' },
  ];

  return (
    <div>
      {/* Search input */}
      <div className="mb-6">
        <div className="relative">
          <svg className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events and businesses..."
            className="w-full pl-12 pr-4 py-3 text-base bg-white border border-gray-200 rounded-xl focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none shadow-sm"
            autoFocus
          />
        </div>
      </div>

      {/* Filter pills */}
      <div className="feed-tabs mb-6">
        {filters.map(filter => (
          <button
            key={filter.id}
            onClick={() => handleFilterChange(filter.id)}
            className={`tab-pill ${activeFilter === filter.id ? 'tab-pill--active' : 'tab-pill--inactive'}`}
            data-filter={filter.id}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Results */}
      <div id="searchResults">
        {!query || query.length < 2 ? (
          <div className="post text-center py-12">
            <p className="text-gray-500">Start typing to search...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="post text-center py-12">
            <p className="text-gray-500">No results found.</p>
          </div>
        ) : (
          <>
            <p className="text-gray-500 text-sm mb-6">
              Found {results.length} result{results.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {results.map((result, index) => {
                if (result.type === 'event') {
                  const business = businesses.find(b => b.id === result.data.businessId);
                  const location = locations.find(l => l.id === result.data.locationId);
                  return (
                    <EventCard
                      key={`event-${result.data.id}-${index}`}
                      event={result.data}
                      business={business}
                      location={location}
                      analytics={analytics}
                    />
                  );
                } else {
                  const location = locations.find(l => l.id === result.data.locationId);
                  return (
                    <BusinessCard
                      key={`business-${result.data.id}-${index}`}
                      business={result.data}
                      location={location}
                      analytics={analytics}
                    />
                  );
                }
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
